<template>
    <div class="min-h-[calc(100vh-3.5rem)] w-full bg-gray-50 px-6 py-6">
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 w-full">
            <div
                v-for="card in cards"
                :key="card.title"
                class="group flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md hover:border-gray-300"
            >
                <div class="flex items-center justify-between">
                    <h3 class="text-sm font-semibold text-dark-primary">{{ card.title }}</h3>
                    <span
                        v-if="card.badge"
                        class="rounded-full bg-accent-primary/10 px-2 py-0.5 text-xs font-medium text-accent-primary"
                    >{{ card.badge }}</span>
                </div>
                <p class="mt-1 text-xs text-gray-500">{{ card.description }}</p>

                <div class="mt-4 flex items-end gap-6">
                    <div v-for="stat in card.stats" :key="stat.label">
                        <div class="text-3xl font-semibold tabular-nums text-gray-900">{{ formatNumbersWithCommas(stat.value) }}</div>
                        <div class="text-xs uppercase tracking-wide text-gray-500">{{ stat.label }}</div>
                    </div>
                </div>

                <div class="mt-4 border-t border-gray-100 pt-3 text-sm text-gray-600">
                    <span class="text-gray-400">{{ card.footerLabel }}</span>
                    <span class="ml-1">{{ card.footer || '—' }}</span>
                </div>

                <button
                    type="button"
                    class="mt-4 inline-flex items-center justify-center gap-1 rounded-lg bg-dark-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-dark-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2"
                    @click="card.open"
                >
                    Launch
                    <span class="transition-transform group-hover:translate-x-0.5">→</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { getResearchDashboardData } from '@/services/Company';
import { getUrlReviewCounts, getUrlDiscoveryRuns } from '@/services/UrlReview';
import { formatNumbersWithCommas } from '@/composables/FormatNumbers';
import { formatDateTwoDigitYear } from '@/composables/formatDate';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import ResearchAssignments from '../research_assignments/ResearchAssignments.vue';

const store = useStore()
const router = useRouter()
const isLoading = ref(false)

const missingZiOrUrlCount = ref(0)
const vetUrlCount = ref(0)
const missingZiOrUrlLastSession = ref(null)
const vetUrlLastSession = ref(null)
const urlReviewLastSession = ref(null)

// The URL Review queue's endpoints are limited to the groups that can mark a
// URL vetted, so the card is too (the same groups as the /url-review route).
const URL_REVIEW_GROUPS = ['isSuperuser', 'isDev', 'isManagement', 'isAccountManager', 'isAdvancedResearch', 'isAccountStaff']
const canReviewUrls = computed(() => {
    const permissions = store.getters['auth/showUserPermissions'] || {}
    return URL_REVIEW_GROUPS.some((group) => permissions[group])
})
const urlReviewPending = ref({ review: 0, no_match: 0 })
const latestUrlRun = ref(null)

async function loadCounts(){
    const token = store.state.auth.token
    const result = await getResearchDashboardData(token)
    if(result.status == 200){
        missingZiOrUrlCount.value = result.data.missing_url_or_zoom_info_data.company_count
        vetUrlCount.value = result.data.vet_url_data.company_count
        missingZiOrUrlLastSession.value = result.data.missing_url_or_zoom_info_data.most_recent_session
        vetUrlLastSession.value = result.data.vet_url_data.most_recent_session
        urlReviewLastSession.value = result.data.url_review_data?.most_recent_session
    }
}

async function loadUrlReview(){
    const token = store.state.auth.token
    try {
        const [counts, runs] = await Promise.all([getUrlReviewCounts(token), getUrlDiscoveryRuns(token)])
        const pending = { review: 0, no_match: 0 }
        for (const row of counts.counts) {
            if (row.status === 'pending') pending[row.kind] = row.count
        }
        urlReviewPending.value = pending
        latestUrlRun.value = runs[0] || null
    } catch (e) {
        // The card keeps its zeros; the page itself reports errors.
    }
}

function openMissingUrlOrZoomInfo(){
    router.push({path:'/missing_url_or_zoom_info'})
}

function openVetUrls(){
    router.push({path:'/vet_urls'})
}

function openUrlReview(){
    router.push({path:'/url-review'})
}

function lastSession(session) {
    // The API serializes "no session yet" as an object of nulls.
    if (!session || !session.session_end) return null
    return `${formatDateTwoDigitYear(session.session_end)} · ${formatNumbersWithCommas(session.total_processed)} processed`
}

const cards = computed(() => {
    const list = [
        {
            title: 'Missing URL or ZoomInfo',
            description: 'Companies missing a website or a ZoomInfo match.',
            stats: [{ label: 'Companies', value: missingZiOrUrlCount.value }],
            footerLabel: 'Last session',
            footer: lastSession(missingZiOrUrlLastSession.value),
            open: openMissingUrlOrZoomInfo,
        },
        {
            title: 'Vet URLs',
            description: 'Websites waiting to be confirmed as the company\'s own.',
            stats: [{ label: 'Companies', value: vetUrlCount.value }],
            footerLabel: 'Last session',
            footer: lastSession(vetUrlLastSession.value),
            open: openVetUrls,
        },
    ]
    if (canReviewUrls.value) {
        const run = latestUrlRun.value
        list.push({
            title: 'URL Review',
            description: 'Companies a URL-discovery run couldn\'t match on its own.',
            badge: run ? (run.status === 'running' ? `Run ${run.id} running` : `Latest: run ${run.id}`) : null,
            stats: [
                { label: 'Need review', value: urlReviewPending.value.review },
                { label: 'No match', value: urlReviewPending.value.no_match },
            ],
            footerLabel: 'Last session',
            footer: lastSession(urlReviewLastSession.value),
            open: openUrlReview,
        })
    }
    return list
})

loadCounts()
if (canReviewUrls.value) loadUrlReview()


</script>